import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";

import { searchGatewayRetailers } from "../../../services/missingReports";

export const GATEWAY_RETAILER_SEARCH_DEBOUNCE_MS = 250;

function GatewayRetailerLookupAutocomplete({
  retailerId = "",
  inputSeedLabel = "",
  onSelect,
  onClear,
  label = "Lookup retailer",
  helperText,
  sx,
  wrapInBox = false,
}) {
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupOptions, setLookupOptions] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    if (!lookupQuery.trim() && String(inputSeedLabel || "").trim()) {
      setLookupQuery(inputSeedLabel);
    }
  }, [inputSeedLabel, lookupQuery]);

  useEffect(() => {
    const query = lookupQuery.trim();
    if (!query) {
      setLookupOptions([]);
      return;
    }

    let active = true;
    const timer = setTimeout(() => {
      async function runSearch() {
        setLookupLoading(true);
        try {
          const results = await searchGatewayRetailers(query);
          if (!active) {
            return;
          }
          setLookupOptions(results);
        } catch (error) {
          if (!active) {
            return;
          }
          setLookupOptions([]);
        } finally {
          if (active) {
            setLookupLoading(false);
          }
        }
      }

      runSearch();
    }, GATEWAY_RETAILER_SEARCH_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [lookupQuery]);

  const selectedOption = useMemo(() => {
    const matched = lookupOptions.find((option) => option.id === retailerId);
    if (matched) {
      return matched;
    }
    if (!retailerId) {
      return null;
    }
    return { id: retailerId, label: inputSeedLabel || "Selected retailer" };
  }, [lookupOptions, retailerId, inputSeedLabel]);

  const autocomplete = (
    <Autocomplete
      sx={sx}
      openOnFocus
      clearOnBlur={false}
      options={lookupOptions}
      value={selectedOption}
      inputValue={lookupQuery}
      filterOptions={(values) => values}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option?.label || ""}
      onChange={(_, option) => {
        if (option) {
          setLookupQuery(option.label || "");
          onSelect?.(option);
          return;
        }
        onClear?.();
      }}
      onInputChange={(_, nextValue, reason) => {
        if (reason !== "input" && reason !== "clear") {
          return;
        }
        setLookupQuery(nextValue);
        if (reason === "clear") {
          onClear?.();
        }
      }}
      loading={lookupLoading}
      noOptionsText="No matching retailers found."
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {lookupLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  if (!wrapInBox) {
    return autocomplete;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Box sx={{ flex: 1 }}>{autocomplete}</Box>
    </Box>
  );
}

export default GatewayRetailerLookupAutocomplete;
